// BoxComponent.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

/* ───────────────── ORDER + ASSETS ───────────────── */
const ORDER = ["dollar", "anything", "bitcoin", "gold", "free"];

const ASSETS = {
  dollar: {
    begining: "/box-5/Dollar/Beginning-Dollar-4.webm",
    beforeSelection: "/box-5/Dollar/Dollar-BeforeSelection-5.webm",
    afterSelection: "/box-5/Dollar/Dollar-AfterSelection-5.webm",
    reverse: "/box-5/Dollar/Dollar-Reverse-3.webm",
  },
  anything: {
    begining: "/box-5/Anything/Beginning-Anything-2.webm",
    beforeSelection: "/box-5/Anything/Anything-BeforeSelection-5.webm",
    afterSelection: "/box-5/Anything/Anything-AfterSelection-5.webm",
    reverse: "/box-5/Anything/Anything-Reverse-3.webm",
  },
  bitcoin: {
    begining: "/box-5/Bitcoin/Beginning-Bitcoin-4.webm",
    beforeSelection: "/box-5/Bitcoin/Bitcoin-BeforeSelection-5.webm",
    afterSelection: "/box-5/Bitcoin/Bitcoin-AfterSelection-5.webm",
    reverse: "/box-5/Bitcoin/Bitcoin-Reverse-3.webm",
  },
  gold: {
    begining: "/box-5/Gold/Beginning-Gold-4.webm",
    beforeSelection: "/box-5/Gold/Gold-BeforeSelection-5.webm",
    afterSelection: "/box-5/Gold/Gold-AfterSelection-5.webm",
    reverse: "/box-5/Gold/Gold-Reverse-3.webm",
  },
  free: {
    begining: "/box-5/Free/Beginning-Free-4.webm",
    beforeSelection: "/box-5/Free/Free-BeforeSelection-5.webm",
    afterSelection: "/box-5/Free/Free-AfterSelection-5.webm",
    reverse: "/box-5/Free/Free-Reverse-3.webm",
  },
};

const DEBUG =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("boxdebug") === "1";

/* ───────────────── small helpers ───────────────── */
function normalizeKey(k) {
  if (!k) return null;
  const s = String(k).toLowerCase();
  if (s === "usd") return "dollar";
  return ORDER.includes(s) ? s : null;
}
function playFromStart(video) {
  if (!video) return;
  try {
    video.currentTime = 0;
    const p = video.play?.();
    if (p?.catch) p.catch(() => {});
  } catch {}
}
function holdLastFrame(video) {
  if (!video) return;
  try {
    const d = video.duration;
    if (Number.isFinite(d) && d > 0) {
      video.currentTime = Math.max(0, d - 0.05);
      video.pause();
    }
  } catch {}
}

/* ───────────────── reverse helpers (seek-loop + frame paint) ───────────────── */
function waitForFrame(video) {
  return new Promise((resolve) => {
    if (typeof video?.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => resolve());
      setTimeout(() => resolve(), 60); // guard
      return;
    }
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      requestAnimationFrame(() => resolve());
    };
    video.addEventListener("seeked", onSeeked, { once: true });
  });
}
function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
async function reverseBySeekLoop(
  video,
  { durationMs = 900, steps = 36, onDone } = {}
) {
  if (!video) {
    onDone?.();
    return;
  }
  try {
    video.pause?.();
  } catch {}
  let startT = Number(video.currentTime) || 0;
  if (startT <= 0.001) {
    onDone?.();
    return;
  }

  const times = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const e = easeInOutQuad(t);
    const target = (1 - e) * startT; // from startT down to 0
    times.push(Math.max(0, target));
  }
  const perStep = Math.max(8, Math.floor(durationMs / Math.max(1, steps)));

  for (let i = 0; i < times.length; i++) {
    try {
      video.currentTime = times[i];
    } catch {}
    await waitForFrame(video);
    if (i < times.length - 1) {
      await new Promise((r) => setTimeout(r, perStep));
    }
  }

  try {
    video.currentTime = 0;
    video.pause?.();
  } catch {}
  onDone?.();
}
function reverseFromEndBySeekLoop(video, opts = {}) {
  if (!video) {
    opts?.onDone?.();
    return;
  }
  const startReverse = () => {
    let d = video.duration;
    if (!Number.isFinite(d) || d <= 0) d = video.currentTime || 1;
    try {
      video.pause?.();
      video.currentTime = Math.max(0, d - 0.05);
    } catch {}
    reverseBySeekLoop(video, opts);
  };

  if (video.readyState >= 1 && Number.isFinite(video.duration)) {
    startReverse();
  } else {
    const once = () => {
      video.removeEventListener("loadedmetadata", once);
      startReverse();
    };
    video.addEventListener("loadedmetadata", once, { once: true });
    try {
      video.load?.();
    } catch {}
  }
}

/* ───────────────── operator events (window + BroadcastChannel) ───────────────── */
function useOperatorEvents(onEvent) {
  useEffect(() => {
    const names = [
      "SHOW_BOX",
      "BOX/SHOW_BEGINNING",
      "BOX/HIGHLIGHT",
      "BOX/SELECT",
      "BOX/BACK",
      "BOX/RESET",
    ];
    const h = (e) => onEvent?.(e.type, e.detail || {});
    names.forEach((n) => window.addEventListener(n, h));

    let bc;
    try {
      bc = new BroadcastChannel("operator-events");
      bc.onmessage = (msg) => {
        const { actionId, payload } = msg?.data || {};
        if (actionId && names.includes(actionId)) onEvent?.(actionId, payload);
      };
    } catch {}

    return () => {
      names.forEach((n) => window.removeEventListener(n, h));
      try {
        bc?.close();
      } catch {}
    };
  }, [onEvent]);
}

/* ───────────────── ItemLayer (with reverse asset) ───────────────── */
const ItemLayer = forwardRef(function ItemLayer(
  {
    sources,
    phase,
    onIntroEnded,
    onBeforeEnded,
    onAfterEnded,
    onReverseEnded,
    zIndex,
  },
  ref
) {
  const beginRef = useRef(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const reverseRef = useRef(null); // NEW

  const showBegin =
    phase === "intro" ||
    phase === "idle" ||
    (!sources.beforeSelection && !sources.afterSelection);
  const showBefore = phase === "before";
  const showAfter = phase === "after";
  const showReverse = phase === "reverse"; // NEW

  useImperativeHandle(ref, () => ({
    // Fallback reverse (used only if no reverse asset exists)
    reverse(which = "before", cb, opts = {}) {
      const target =
        which === "after"
          ? afterRef.current
          : which === "before"
          ? beforeRef.current
          : null;
      reverseBySeekLoop(target, {
        durationMs: opts.durationMs ?? 900,
        steps: opts.steps ?? 36,
        onDone: cb,
      });
    },
    reverseFromEnd(which = "before", cb, opts = {}) {
      const target =
        which === "after"
          ? afterRef.current
          : which === "before"
          ? beforeRef.current
          : null;
      reverseFromEndBySeekLoop(target, {
        durationMs: opts.durationMs ?? 900,
        steps: opts.steps ?? 36,
        onDone: cb,
      });
    },
    playReverse(cb) {
      if (!reverseRef.current) {
        cb?.();
        return;
      }
      try {
        reverseRef.current.currentTime = 0;
        const p = reverseRef.current.play?.();
        if (p?.catch) p.catch(() => {});
      } catch {}
      // cb will be called from onEnded via onReverseEnded
    },
  }));

  useEffect(() => {
    if (phase === "intro") playFromStart(beginRef.current);
    else if (phase === "before") playFromStart(beforeRef.current);
    else if (phase === "after") playFromStart(afterRef.current);
    else if (phase === "reverse") playFromStart(reverseRef.current);
    else if (phase === "idle") {
      holdLastFrame(beginRef.current);
      beforeRef.current?.pause?.();
      afterRef.current?.pause?.();
      reverseRef.current?.pause?.();
    }
  }, [phase]);

  const onBeginEnded = () => {
    holdLastFrame(beginRef.current);
    onIntroEnded?.();
  };

  const layerBase =
    "absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ease-in-out pointer-events-none";

  return (
    <div
      className="absolute inset-0"
      style={{ zIndex, background: "transparent" }}
    >
      {sources.begining && (
        <video
          ref={beginRef}
          src={sources.begining}
          muted
          playsInline
          preload="auto"
          onEnded={onBeginEnded}
          className={`${layerBase} ${showBegin ? "opacity-100" : "opacity-0"}`}
          style={{ backgroundColor: "transparent" }}
        />
      )}
      {sources.beforeSelection && (
        <video
          ref={beforeRef}
          src={sources.beforeSelection}
          muted
          playsInline
          preload="auto"
          onEnded={() => {
            holdLastFrame(beforeRef.current);
            onBeforeEnded?.();
          }}
          className={`${layerBase} ${showBefore ? "opacity-100" : "opacity-0"}`}
          style={{ backgroundColor: "transparent" }}
        />
      )}
      {sources.afterSelection && (
        <video
          ref={afterRef}
          src={sources.afterSelection}
          muted
          playsInline
          preload="auto"
          onEnded={() => {
            holdLastFrame(afterRef.current);
            onAfterEnded?.();
          }}
          className={`${layerBase} ${showAfter ? "opacity-100" : "opacity-0"}`}
          style={{ backgroundColor: "transparent" }}
        />
      )}
      {sources.reverse && (
        <video
          ref={reverseRef}
          src={sources.reverse}
          muted
          playsInline
          preload="auto"
          onEnded={() => {
            holdLastFrame(reverseRef.current);
            onReverseEnded?.();
          }}
          className={`${layerBase} ${
            showReverse ? "opacity-100" : "opacity-0"
          }`}
          style={{ backgroundColor: "transparent" }}
        />
      )}
    </div>
  );
});

/* ───────────────── Main BoxComponent ───────────────── */
export default function BoxComponent() {
  // per-item phase: "intro" | "idle" | "before" | "after" | "reverse"
  const [phases, setPhases] = useState(() => {
    const obj = {};
    ORDER.forEach((k) => (obj[k] = "idle"));
    return obj;
  });

  // intro sequencing
  const [introIndex, setIntroIndex] = useState(null);

  // active action top (highlight/select should be above blur & others)
  const [actionTopKey, setActionTopKey] = useState(null);

  // global blur
  const [blurOn, setBlurOn] = useState(false);

  // remember last action key so BACK can work without payload
  const lastActionKeyRef = useRef(null);

  // refs for item layers
  const itemRefs = useRef(
    Object.fromEntries(ORDER.map((k) => [k, React.createRef()]))
  );

  const startIntro = () => {
    const base = {};
    ORDER.forEach((k) => (base[k] = "idle"));
    setPhases(base);

    setIntroIndex(0);
    setActionTopKey(null);
    lastActionKeyRef.current = null;

    setPhases((p) => ({ ...p, [ORDER[0]]: "intro" }));
    setBlurOn(true); // blur during beginning sequence
  };

  useEffect(() => {
    startIntro();
  }, []);

  const rememberActionKey = (key) => {
    lastActionKeyRef.current = key;
    setActionTopKey(key);
  };
  const resolveKeyFromPayload = (payload) =>
    normalizeKey(payload?.key) || lastActionKeyRef.current || null;

  // operator events (window + BroadcastChannel)
  useOperatorEvents((actionId, payload) => {
    switch (actionId) {
      case "BOX/SHOW_BEGINNING":
        startIntro();
        break;

      case "BOX/HIGHLIGHT": {
        const key = resolveKeyFromPayload(payload);
        if (!key) break;
        setPhases((p) => ({ ...p, [key]: "before" }));
        rememberActionKey(key);
        setBlurOn(true); // stays even after before ends
        break;
      }

      case "BOX/SELECT": {
        const key = resolveKeyFromPayload(payload);
        if (!key) break;
        // SELECT flow: play after; blur stays until 'after' ends
        setPhases((p) => ({ ...p, [key]: "after" }));
        rememberActionKey(key);
        setBlurOn(true);
        break;
      }

      case "BOX/BACK": {
        const key = resolveKeyFromPayload(payload);
        if (!key) break;

        const phase = phases[key];
        const ref = itemRefs.current[key]?.current;
        if (!ref) break;

        // keep item above others while reversing
        setActionTopKey(key);

        const hasReverseAsset = !!ASSETS[key]?.reverse;

        if (hasReverseAsset) {
          // Use the dedicated reverse clip regardless of current phase
          setPhases((p) => ({ ...p, [key]: "reverse" }));
          setBlurOn(true); // blur during reverse; off in onReverseEnded
          // phase change triggers play; calling explicitly is optional:
          // ref.playReverse();
        } else {
          // Fallback to seek-loop reverse
          if (phase === "after") {
            ref.reverse(
              "after",
              () => {
                setPhases((p) => ({ ...p, [key]: "before" }));
                ref.reverseFromEnd(
                  "before",
                  () => {
                    setPhases((p2) => ({ ...p2, [key]: "idle" }));
                    setActionTopKey(null);
                    setBlurOn(false);
                  },
                  { durationMs: 1100, steps: 40 }
                );
              },
              { durationMs: 1100, steps: 40 }
            );
          } else if (phase === "before") {
            ref.reverseFromEnd(
              "before",
              () => {
                setPhases((p) => ({ ...p, [key]: "idle" }));
                setActionTopKey(null);
                setBlurOn(false);
              },
              { durationMs: 1100, steps: 40 }
            );
          } else {
            // idle/intro → ensure idle
            setPhases((p) => ({ ...p, [key]: "idle" }));
            setActionTopKey(null);
            setBlurOn(false);
          }
        }
        break;
      }

      case "BOX/RESET": {
        const base = {};
        ORDER.forEach((k) => (base[k] = "idle"));
        setPhases(base);
        setIntroIndex(null);
        setActionTopKey(null);
        lastActionKeyRef.current = null;
        setBlurOn(false);
        break;
      }

      default:
        break;
    }
  });

  // intro progress
  const handleIntroEnded = (name) => {
    setPhases((p) => ({ ...p, [name]: "idle" }));
    setIntroIndex((idx) => {
      if (idx == null) return null;
      const next = idx + 1;
      if (next >= ORDER.length) {
        setBlurOn(false); // remove blur after intro finishes
        setActionTopKey(null);
        return null;
      }
      const nextKey = ORDER[next];
      setPhases((p2) => ({ ...p2, [nextKey]: "intro" }));
      setActionTopKey(null);
      setBlurOn(true);
      return next;
    });
  };

  const onBeforeEnded = () => {
    // blur remains after HIGHLIGHT
  };
  const onAfterEnded = () => {
    // blur turns off after SELECT finishes
    setBlurOn(false);
    setActionTopKey(null);
  };
  const onReverseEnded = (key) => {
    // reverse finished → return to idle, clear effects
    setPhases((p) => ({ ...p, [key]: "idle" }));
    setActionTopKey(null);
    setBlurOn(false);
  };

  // z-index: base 1000 + index; blur 1500; active intro/selected 2000
  const zIndexFor = (key) => {
    const base = 1000 + ORDER.indexOf(key);
    const isIntroTop = introIndex != null && ORDER[introIndex] === key;
    const isActionTop = actionTopKey === key;
    if (isIntroTop || isActionTop) return 2000;
    return base;
  };

  const debugText = useMemo(
    () =>
      DEBUG
        ? ORDER.map((k) => `${k}:${phases[k]}`).join(" | ") +
          ` | top=${actionTopKey ?? "(intro/none)"} | last=${
            lastActionKeyRef.current ?? "none"
          }`
        : null,
    [phases, actionTopKey]
  );

  return (
    <div className="w-full h-full flex items-center justify-center">
      {/* Full-bleed stage; alpha preserved */}
      <div className="relative w-full max-w-[1400px] aspect-video overflow-hidden rounded-xl">
        {/* Blur overlay */}
        <div
          className={`absolute inset-0 pointer-events-none transition-opacity duration-250 ease-linear ${
            blurOn ? "opacity-100" : "opacity-0"
          }`}
          style={{
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            background: "transparent",
            zIndex: 1500,
            willChange: "opacity",
          }}
        />

        {/* Items (stacked, full-frame) */}
        {ORDER.map((key) => (
          <ItemLayer
            key={key}
            ref={itemRefs.current[key]}
            sources={ASSETS[key]}
            phase={phases[key]}
            onIntroEnded={() => handleIntroEnded(key)}
            onBeforeEnded={onBeforeEnded}
            onAfterEnded={onAfterEnded}
            onReverseEnded={() => onReverseEnded(key)}
            zIndex={zIndexFor(key)}
          />
        ))}

        {DEBUG && (
          <div className="absolute bottom-2 left-2 text-xs text-white/80 bg-black/50 px-2 py-1 rounded">
            introIndex: {String(introIndex)} — blurOn: {String(blurOn)} —{" "}
            {debugText}
          </div>
        )}
      </div>
    </div>
  );
}
